<template>
  <BasePage :pageTitle="'Shift Details'" show-menu show-footer back-button>
    <Card v-if="!loading" :title="`${shiftInfo.dayName} - ${shiftInfo.period}`" :title-level="1" margin="">
        <!-- Time and Location Section -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="datagrid">
              <div class="datagrid-item">
                <div class="datagrid-title">{{ __DICT__.time }}</div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div class="datagrid-content">{{ shiftInfo.Start }} - {{ shiftInfo.End }}</div>
              </div>
              <div class="datagrid-item">
                <div class="datagrid-title">{{ __DICT__.role }}</div>
                <div class="datagrid-content">
                  <span
                      class="badge text-white"
                      :class="getRoleBadgeClass(shiftInfo.Role)"
                  >
                    {{ shiftInfo.Role }}
                  </span>
                </div>
              </div>
              <div class="datagrid-item">
                <div class="datagrid-title">{{ __DICT__.location }}</div>
                <div class="datagrid-content">
                  <a
                      v-if="shiftInfo['URL or Lat/Long']"
                      :href="getLocationUrl(shiftInfo['URL or Lat/Long'])"
                      target="_blank"
                      class="text-decoration-none"
                  >
                    {{ shiftInfo.Location }}
                    <IconExternalLink class="icon icon-inline ms-1" :size="24" />
                  </a>
                  <span v-else>{{ shiftInfo.Location }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Team Members Section (if showing all shifts) -->
        <div v-if="shiftInfo.reps && shiftInfo.reps.length > 0" class="mt-4">
          <h3 class="mb-3">{{ __DICT__.schedule.teamMembers }}</h3>
          <div class="row row-cards">
            <div v-for="rep in shiftInfo.reps" :key="rep.id" class="col-md-6 col-lg-4">
              <router-link :to="'/rep/'+rep.id">
                <div class="card card-sm">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-auto">
                      <span class="avatar">
                        {{ getInitials(rep.name) }}
                      </span>
                      </div>
                      <div class="col">
                        <div class="font-weight-medium">{{ rep.name }}</div>
                        <div class="text-muted small">{{ rep.role }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </router-link>
            </div>
          </div>
        </div>

    </Card>

    <!-- Loading State -->
    <Card v-else margin="">
      <LoadingSpinner centered />
    </Card>
  </BasePage>
</template>

<script setup>
import {ref, watch, onMounted} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import BasePage from '@/components/BasePage.vue';
import {useWeekStore} from '@/stores/week.js';
import {api} from '@/services/api.js';
import { decodeBase64Url } from '@/services/base64.js';
import { Card, LoadingSpinner } from '@/components/atomic';
import { IconExternalLink } from '@tabler/icons-vue';
import { useConfigFlag } from '@/composables/useConfigFlag.js';
import { ROTA_DAYS, PERIODS } from '@/constants/rota.js';

const router = useRouter();
const route = useRoute();
const weekStore = useWeekStore();
const loading = ref(true);
const shiftInfo = ref({});
const shiftNotes = ref('');

const { enabled: rotaEnabled, loading: flagLoading } = useConfigFlag('rota-editor-enabled');

// Previous utility functions remain the same
const getColorFromString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['blue', 'green', 'red', 'yellow', 'purple', 'azure', 'orange', 'teal', 'cyan', 'indigo'];
  return colors[Math.abs(hash) % colors.length];
};

const getRoleBadgeClass = (role) => {
  if (role) {
    return `bg-${getColorFromString(role)}`;
  } else {
    return `bg-primary`
  }
};

const getLocationUrl = (urlOrLatLong) => {
  if (urlOrLatLong?.startsWith('http')) {
    return urlOrLatLong;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(urlOrLatLong)}`;
};

const getInitials = (name) => {
  console.log(name)
  try {
    return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase();
  } catch (e) {
    return 'ERR';
  }
};

// Updated shift finding logic
const findShiftInData = (allRepsData, shiftId) => {
  const [role, location, start, end, targetDayName, targetPeriod] = shiftId.split('~');

  // If we have the exact day and period from the ID, we can look there directly
  for (const rep of allRepsData) {
    const dayData = rep[targetDayName];
    if (dayData && dayData[targetPeriod]) {
      const shift = dayData[targetPeriod];
      if (
          shift?.Role === role &&
          shift?.Location === location &&
          shift?.Start === start &&
          shift?.End === end
      ) {
        // Found matching shift, now collect all reps who have this same shift
        const reps = allRepsData
            .filter(r =>
                r[targetDayName]?.[targetPeriod]?.Role === role &&
                r[targetDayName]?.[targetPeriod]?.Location === location &&
                r[targetDayName]?.[targetPeriod]?.Start === start &&
                r[targetDayName]?.[targetPeriod]?.End === end
            )
            .map(r => ({
              name: r.Name,
              id: r['User ID'],
              role: r["Rep Sheet Info - Remit"]
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

        return {
          ...shift,
          dayName: targetDayName,
          period: targetPeriod,
          reps
        };
      }
    }
  }

  return null;
};

// Find shift from DB API data
const findShiftFromDb = async (shiftId) => {
  const [role, location, start, end, targetDayName, targetPeriod] = shiftId.split('~');

  const brand = __DICT__.brand === 'wasteland' ? 'wasteland' : 'nuco';
  const shifts = await api.get(`/api/rota?weekId=${weekStore.weekId}&brand=${brand}`);

  if (!Array.isArray(shifts)) return null;

  const dayIndex = ROTA_DAYS.indexOf(targetDayName);
  const periodIndex = PERIODS.indexOf(targetPeriod);

  const match = shifts.find(s =>
    s.role === role &&
    (s.location || '') === (location || '') &&
    s.start === start &&
    s.end === end &&
    s.dayIndex === dayIndex &&
    s.period === periodIndex
  );

  if (!match) return null;

  // Look up rep names from repData
  const repD = await api.get(`/api/repData/${weekStore.weekId}`);
  const reps = match.assignedReps.map(repId => {
    const info = repD.find(r => String(r['GBS ID']) === String(repId));
    return {
      name: info?.Name || 'Unknown',
      id: repId,
      role: info?.['Week Remit'] || '',
    };
  }).sort((a, b) => a.name.localeCompare(b.name));

  return {
    Role: match.role,
    Location: match.location,
    'URL or Lat/Long': match.locationUrl,
    Start: match.start,
    End: match.end,
    dayName: targetDayName,
    period: targetPeriod,
    reps,
  };
};

// Load shift data
onMounted(async () => {
  try {
    // Validate shiftId parameter exists
    if (!route.params.shiftId) {
      console.error('[ShiftPage] Missing shiftId parameter, redirecting');
      router.replace('/my-week');
      return;
    }

    // Safely decode the shift parameter
    let shiftId;
    try {
      shiftId = decodeBase64Url(route.params.shiftId);
    } catch (decodeError) {
      console.error('[ShiftPage] Failed to decode shiftId:', decodeError);
      router.replace('/my-week');
      return;
    }

    // Wait for flag to resolve
    if (flagLoading.value) {
      await new Promise(resolve => {
        const stop = watch(flagLoading, (val) => {
          if (!val) { stop(); resolve(); }
        });
      });
    }

    let foundShift = null;

    if (rotaEnabled.value) {
      foundShift = await findShiftFromDb(shiftId);
    } else {
      let docType = 'rep';
      if (__DICT__.brand === 'wasteland') {
        docType = 'wl-rep';
      }
      const response = await api.get(`/api/week/${docType}/${weekStore.weekId}`);
      const allRepsData = response.weekDocs[0].lastJsonData.jsonData;
      foundShift = findShiftInData(allRepsData, shiftId);
    }

    if (foundShift) {
      shiftInfo.value = foundShift;
    } else {
      console.error('Shift not found');
      router.push('/profile');
    }

    loading.value = false;
  } catch (error) {
    console.error('Failed to load shift data:', error);
    loading.value = false;
  }
});
</script>